import { MessageOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Badge, Button, Divider, Flex, Popover, Skeleton, Typography } from "antd";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { actions } from "redux-router5";
import { CommentInboxItem } from "../../common/notifications/CommentInboxItem";
import { resolveNotificationTarget } from "../../common/notifications/notificationTarget";
import type { NotificationItem } from "../../common/utils/useNotifications";
import { useNotifications } from "../../common/utils/useNotifications";
import { routeNames } from "../common/routes";
import type { AppDispatch } from "../store";
import styles from "./CommentInbox.module.css";
import { commentNotificationsAdapter } from "./commentNotificationsAdapter";

export function CommentInbox({ intl }: { intl: IntlShape }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead } =
    useNotifications(commentNotificationsAdapter);

  const openNotification = (notification: NotificationItem) => {
    if (notification.id && !notification.read_at) {
      markAsRead(notification.id);
    }
    const target = resolveNotificationTarget(notification);
    if (target) {
      dispatch(actions.navigateTo(routeNames.TICKET_VIEW, target));
    }
    setOpen(false);
  };

  const panelContent = (
    <Flex vertical className={styles.panel}>
      <Flex align="center" justify="space-between" className={styles.panelHeader}>
        <Typography.Text strong>
          {intl.formatMessage({ id: "comment_inbox_title" })}
        </Typography.Text>
        <Button
          type="link"
          size="small"
          disabled={unreadCount === 0}
          onClick={markAllAsRead}
        >
          {intl.formatMessage({ id: "comment_inbox_mark_all_read" })}
        </Button>
      </Flex>
      <Divider className={styles.headerDivider} />

      {loading && (
        <Flex vertical className={styles.listContainer}>
          {[1, 2, 3].map((key) => (
            <Flex vertical key={key} className={styles.skeletonItem}>
              <Skeleton avatar={{ size: 32 }} paragraph={{ rows: 1 }} active />
            </Flex>
          ))}
        </Flex>
      )}

      {!loading && error && (
        <Flex vertical align="center" className={styles.emptyState}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: "comment_inbox_failed" })}
          </Typography.Text>
        </Flex>
      )}

      {!loading && !error && notifications.length === 0 && (
        <Flex vertical align="center" className={styles.emptyState}>
          <MessageOutlined className={styles.emptyIcon} />
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: "comment_inbox_empty" })}
          </Typography.Text>
        </Flex>
      )}

      {!loading && !error && notifications.length > 0 && (
        <Flex vertical className={styles.listContainer}>
          {notifications.map((notification) => (
            <CommentInboxItem
              key={notification.id}
              notification={notification}
              intl={intl}
              onOpen={openNotification}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );

  return (
    <Popover
      content={panelContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      classNames={{ root: styles.popoverOverlay }}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<MessageOutlined />}
          className={styles.inboxButton}
          aria-label={intl.formatMessage({ id: "comment_inbox_title" })}
        />
      </Badge>
    </Popover>
  );
}
